import React from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Full-screen blocker shown when the TENANT's licence has lapsed or been
 * suspended (server returns 402 with license_expired: true).
 *
 * Deliberately not a variant of SessionExpiredModal. That one offers a password
 * field because re-authenticating genuinely fixes the problem. Here it does not:
 * the account is fine, the subscription is not, and a password box would invite
 * the user to try — and fail — repeatedly. The only actions available are to
 * contact Journey.AI or to sign out.
 *
 * The copy also has to be clearer than "session expired" about data: nothing has
 * been deleted and nothing is lost, which is the first thing anyone locked out
 * of their accounting system wants to know.
 */
const LicenseExpiredModal = () => {
  const { licenseBlock, logout } = useAuth();

  if (!licenseBlock) return null;

  const suspended = licenseBlock.reason === 'suspended';
  const company = licenseBlock.company_name;
  const expiredAt = licenseBlock.expired_at ? new Date(licenseBlock.expired_at) : null;
  const expiredAtLabel = expiredAt && !Number.isNaN(expiredAt.getTime())
    ? expiredAt.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const handleSignOut = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 w-full max-w-md mx-4">
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-5 rounded-full bg-amber-100 dark:bg-amber-900/40">
          <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
        </div>

        <h2 className="text-lg font-semibold text-center text-gray-900 dark:text-white mb-2">
          {suspended ? 'Workspace suspended' : 'Subscription expired'}
        </h2>

        <p className="text-sm text-center text-gray-600 dark:text-gray-300 mb-4">
          {suspended
            ? <>Access to {company ? <span className="font-medium">{company}</span> : 'this workspace'} has been suspended.</>
            : (
              <>
                {company ? <span className="font-medium">{company}</span> : 'This workspace'} no longer has an active
                Journey.AI subscription{expiredAtLabel ? <> — it ended on {expiredAtLabel}</> : null}.
              </>
            )}
        </p>

        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 px-4 py-3 mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium text-gray-800 dark:text-gray-100">Your data is safe.</span>{' '}
            Nothing has been deleted. Everything is exactly where you left it and
            will be available again the moment access is restored.
          </p>
        </div>

        <p className="text-sm text-center text-gray-600 dark:text-gray-300 mb-6">
          Please contact Journey.AI to {suspended ? 'resolve this' : 'renew'}.
        </p>

        <button
          type="button"
          onClick={handleSignOut}
          className="w-full py-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-md text-sm font-medium transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default LicenseExpiredModal;
